import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import logo from "../assets/peeko-logo.svg";
import styles from "./AuthShell.module.scss";

/**
 * The centred surface every signed-out state of the panel is drawn on.
 *
 * One component rather than centring repeated per state, because the four things it wraps — the
 * session check, an unreachable API, sign-in, and a signed-in account with no staff role — are all
 * the same screen from the operator's point of view, and they should not each drift into their own
 * idea of where the middle is.
 *
 * The logo is rendered here, outside the card, so that it stays put while the card's contents change
 * between steps. That is what makes the three-step sign-in feel like one screen rather than three.
 *
 * ## The logo is a dark-ink asset, and that decides the colour scheme
 *
 * `assets/peeko-logo.svg` is drawn in `#011111` with no light variant. On a dark surface it would be
 * invisible, so the panel pins itself to the light scheme in `main.tsx` — see the note there. This is
 * a brand-asset constraint rather than a design preference, and it changes the day a light-ink logo
 * exists.
 */
export function AuthShell({
  children,
  footnote,
}: {
  children: ReactNode;
  /** Rendered below the card, outside it — for anything that is not part of the form. */
  footnote?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.surface}>
      <div className={styles.stack}>
        {/* Vite fingerprints and serves this from the app's own bundle rather than the Shopify CDN.
            A sign-in screen that cannot render its own logo without a third-party request is a
            sign-in screen with an availability dependency it does not need. */}
        <img className={styles.logo} src={logo} alt={t("app.logoAlt")} />
        <div className={styles.card}>{children}</div>
        {footnote ? <div className={styles.footnote}>{footnote}</div> : null}
      </div>
    </div>
  );
}
